/**
 * FanLamp Pro / LampSmart Pro advertising codecs.
 *
 * Ported from NicoIIT/ha-ble-adv (custom_components/ble_adv/codecs/fanlamp.py)
 * via the LampApp React Native reference (SmartLampPro-reactjs), where the same
 * code is TypeScript and checked against 90 golden vectors generated by the
 * Python encoders. This file is that TypeScript with the types lifted into
 * JSDoc; keep the two in step, and keep ./codecs.test.js green — it runs the
 * same golden vectors.
 *
 * These lamps are never connected to: every command is a one-shot BLE
 * advertisement that any lamp holding the matching id will act on.
 *
 * The payload rides in AD type 0x03 (complete list of 16-bit service UUIDs),
 * NOT in manufacturer data. See advertiser.js for why that matters.
 *
 * @typedef {object} LampConfig  Persistent identity of one paired lamp (or group).
 * @property {string} codec    Codec id, e.g. 'lampsmart_pro_v3'.
 * @property {number} id       32-bit controller id, drawn at pair time.
 * @property {number} index    Device index 0..15. Part of the ADDRESS, not a
 *   broadcast channel: the reference identifies a device by (codec, id, index).
 *   Two lamps paired to the same id AND index act as one group.
 * @property {number} seed     16-bit packet seed, drawn at pair time then held.
 * @property {number} txCount  Rolling counter, incremented on every command.
 *
 * @typedef {object} EncCmd  A command in the vocabulary of the encoder.
 * @property {number} cmd
 * @property {number} [param]
 * @property {number} [arg0]
 * @property {number} [arg1]
 * @property {number} [arg2]
 *
 * @typedef {object} Advertisement  One complete BLE advertising data structure.
 * @property {number} adFlag   Flags AD value the original app emits (0x19).
 * @property {number} adType   AD type of the payload field: 0x03.
 * @property {Uint8Array} payload  26 bytes.
 */
import { aes128EncryptBlock, crc16, reverseAll, whiten } from './crypto';
const TX_MAX = 125;
const SEED_MAX = 0xfff5;
/** Advance the rolling counter exactly as the reference implementation does. */
export function nextTxCount(txCount) {
  return (txCount + 1) % TX_MAX;
}
export function randomSeed() {
  return 1 + Math.floor(Math.random() * SEED_MAX);
}
export function randomDeviceId() {
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}
const u8 = (n) => n & 0xff;
export class Codec {
  adFlag = 0x19;
  adType = 0x03;
  encode(cmd, conf) {
    const encrypted = this.encrypt(this.body(cmd, conf));
    const header = this.header();
    const payload = new Uint8Array(header.length + encrypted.length);
    payload.set(header, 0);
    payload.set(encrypted, header.length);
    return { adFlag: this.adFlag, adType: this.adType, payload };
  }
}
// ---------------------------------------------------------------------------
// V1 family
// ---------------------------------------------------------------------------
const V1_PREFIX = Uint8Array.from([0xaa, 0x98, 0x43, 0xaf, 0x0b, 0x46, 0x46, 0x46]);
class FanLampV1 extends Codec {
  id;
  label;
  app;
  primary;
  prefix = V1_PREFIX;
  o;
  constructor(o) {
    super();
    this.o = o;
    this.id = o.id;
    this.label = o.label;
    this.app = o.app;
    this.primary = o.primary;
  }
  header() {
    return Uint8Array.from([...this.o.header, ...whiten(reverseAll(this.prefix), 0x6f)]);
  }
  getArg2(cmd, arg2) {
    if (cmd === 0x22) {
      return arg2;
    }
    const applies =
      cmd === 0x28 || (!this.o.arg2OnlyOnPair && ![0x12, 0x13, 0x1e, 0x1f].includes(cmd));
    return applies ? this.o.arg2 : 0;
  }
  body(cmd, conf) {
    const isPair = cmd.cmd === 0x28;
    const uid16 = (conf.id & 0xf0ff) | ((conf.index & 0x0f) << 8);
    const seed8 = conf.seed & 0xff;
    const b = [
      u8(cmd.cmd),
      u8(uid16), // little endian
      u8(uid16 >> 8),
      isPair ? u8(conf.id) : u8(cmd.arg0 ?? 0),
      isPair ? u8(conf.id >> 8) & 0xf0 : u8(cmd.arg1 ?? 0),
      this.getArg2(cmd.cmd, cmd.arg2 ?? 0),
      u8(conf.txCount),
      this.o.arg2 === 0x00 && isPair ? u8(this.o.header[0]) : u8(cmd.param ?? 0),
      this.o.xor1 ? seed8 ^ 1 : seed8 ^ u8(conf.id >>> 16),
      this.o.xor1 ? seed8 ^ 1 : seed8,
      u8(conf.seed >> 8), // big endian
      u8(conf.seed),
    ];
    const crc = crc16(Uint8Array.from(b), (conf.seed ^ 0xffff) & 0xffff);
    b.push(u8(crc >> 8), u8(crc)); // big endian
    return Uint8Array.from(b);
  }
  crc2(body) {
    // 0xA5BE is crc16([0x98,0x43,0xAF,0x0B,0x46], 0xFFFF) -- the fixed prefix.
    return this.o.forcedCrc2 ?? crc16(body, 0xa5be);
  }
  encrypt(body) {
    const c = this.crc2(body);
    return whiten(reverseAll(Uint8Array.from([...body, u8(c >> 8), u8(c)])), 0x0c);
  }
  cww(cold, warm) {
    return { cmd: 0x21, param: 0, arg0: u8(cold), arg1: u8(warm) };
  }
}
/** V1 variant carrying a fixed 0xAA trailer instead of a second CRC. */
class FanLampV1aa extends FanLampV1 {
  prefix = Uint8Array.from([0x55, ...V1_PREFIX]);
  encrypt(body) {
    return whiten(reverseAll(Uint8Array.from([...body, 0xaa])), 0x2b);
  }
}
// ---------------------------------------------------------------------------
// V2 family
// ---------------------------------------------------------------------------
const XBOXES = Uint8Array.from([
  0xb7, 0xfd, 0x93, 0x26, 0x36, 0x3f, 0xf7, 0xcc, 0x34, 0xa5, 0xe5, 0xf1, 0x71, 0xd8, 0x31, 0x15,
  0x04, 0xc7, 0x23, 0xc3, 0x18, 0x96, 0x05, 0x9a, 0x07, 0x12, 0x80, 0xe2, 0xeb, 0x27, 0xb2, 0x75,
  0xd0, 0xef, 0xaa, 0xfb, 0x43, 0x4d, 0x33, 0x85, 0x45, 0xf9, 0x02, 0x7f, 0x50, 0x3c, 0x9f, 0xa8,
  0x51, 0xa3, 0x40, 0x8f, 0x92, 0x9d, 0x38, 0xf5, 0xbc, 0xb6, 0xda, 0x21, 0x10, 0xff, 0xf3, 0xd2,
  0xe0, 0x32, 0x3a, 0x0a, 0x49, 0x06, 0x24, 0x5c, 0xc2, 0xd3, 0xac, 0x62, 0x91, 0x95, 0xe4, 0x79,
  0xe7, 0xc8, 0x37, 0x6d, 0x8d, 0xd5, 0x4e, 0xa9, 0x6c, 0x56, 0xf4, 0xea, 0x65, 0x7a, 0xae, 0x08,
  0xe1, 0xf8, 0x98, 0x11, 0x69, 0xd9, 0x8e, 0x94, 0x9b, 0x1e, 0x87, 0xe9, 0xce, 0x55, 0x28, 0xdf,
  0x8c, 0xa1, 0x89, 0x0d, 0xbf, 0xe6, 0x42, 0x68, 0x41, 0x99, 0x2d, 0x0f, 0xb0, 0x54, 0xbb, 0x16,
]);
const SIGN_KEY_TAIL = [
  0x0d, 0xbf, 0xe6, 0x42, 0x68, 0x41, 0x99, 0x2d, 0x0f, 0xb0, 0x54, 0xbb, 0x16,
];
class FanLampV2 extends Codec {
  id;
  label;
  app;
  primary;
  o;
  constructor(o) {
    super();
    this.o = o;
    this.id = o.id;
    this.label = o.label;
    this.app = o.app;
    this.primary = o.primary;
  }
  header() {
    return Uint8Array.from([0xf0, 0x08]);
  }
  whitenV2(buffer, seed, salt) {
    const out = new Uint8Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) {
      out[i] = XBOXES[((seed + i + 9) & 0x1f) + salt] ^ seed ^ buffer[i];
    }
    return out;
  }
  /** First two bytes of an AES-128-ECB block, little endian; 0 maps to 0xFFFF. */
  sign(block, txCount, seed) {
    const key = Uint8Array.from([u8(seed), u8(seed >> 8), u8(txCount), ...SIGN_KEY_TAIL]);
    const ct = aes128EncryptBlock(key, block);
    const s = ct[0] | (ct[1] << 8);
    return s !== 0 ? s : 0xffff;
  }
  body(cmd, conf) {
    const base = Uint8Array.from([
      ...this.o.pfx,
      u8(conf.txCount),
      u8(this.o.deviceType),
      u8(this.o.deviceType >> 8),
      u8(conf.id),
      u8(conf.id >> 8),
      u8(conf.id >> 16),
      u8(conf.id >>> 24),
      u8(conf.index),
      u8(cmd.cmd),
      0x00,
      u8(cmd.param ?? 0),
      u8(cmd.arg0 ?? 0),
      u8(cmd.arg1 ?? 0),
      u8(cmd.arg2 ?? 0),
    ]);
    const sign = this.o.withSign ? this.sign(base.subarray(1, 17), base[3], conf.seed) : 0;
    return Uint8Array.from([
      ...base,
      u8(sign),
      u8(sign >> 8),
      0x00,
      u8(conf.seed),
      u8(conf.seed >> 8),
    ]);
  }
  encrypt(decoded) {
    const n = decoded.length;
    const seed = decoded[n - 2] | (decoded[n - 1] << 8);
    const mid = this.whitenV2(decoded.subarray(2, n - 2), seed & 0xff, (decoded[1] & 0x3) << 5);
    const obuf = Uint8Array.from([decoded[0], decoded[1], ...mid, decoded[n - 2], decoded[n - 1]]);
    const crc = crc16(obuf, (seed ^ 0xffff) & 0xffff);
    return Uint8Array.from([...obuf, u8(crc), u8(crc >> 8)]); // little endian
  }
  cww(cold, warm) {
    return { cmd: 0x21, arg0: 0, arg1: u8(cold), arg2: u8(warm) };
  }
}
// ---------------------------------------------------------------------------
// Registry.
//
// The (withSign, pfx) pairs come from DYN_CODEC_PARAM_MAP in ha-ble-adv's
// codecs/__init__.py. Note that v2 and v3 are the SAME encoder with different
// parameters -- v3 signs its packets and opens with 0x30, v2 does neither.
// Getting that pairing wrong produces packets no lamp answers.
//
// Order matters: the pairing sweep walks this list, so the encodings each app
// actually documents come first.
// ---------------------------------------------------------------------------
export const LAMP_SMART = 'Lamp Smart Pro';
export const FAN_LAMP = 'Fan Lamp Pro';
/** Every app whose lamps this build can drive. */
export const APPS = [LAMP_SMART, FAN_LAMP];
export const CODECS = [
  new FanLampV2({
    id: 'lampsmart_pro_v3',
    label: 'v3',
    app: LAMP_SMART,
    primary: true,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x30, 0x80, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v2',
    label: 'v2',
    app: LAMP_SMART,
    primary: true,
    deviceType: 0x0100,
    withSign: false,
    pfx: [0x10, 0x80, 0x00],
  }),
  new FanLampV1({
    id: 'lampsmart_pro_v1',
    label: 'v1',
    app: LAMP_SMART,
    primary: true,
    arg2: 0x81,
    arg2OnlyOnPair: true,
    header: [0x77, 0xf8],
  }),
  new FanLampV1aa({
    id: 'lampsmart_pro_v1b',
    label: 'v1b',
    app: LAMP_SMART,
    primary: false,
    arg2: 0x81,
    arg2OnlyOnPair: true,
    header: [0xf9, 0x08],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s1',
    label: 'v3 - s1',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x30, 0x81, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s2',
    label: 'v3 - s2',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x30, 0x82, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s3',
    label: 'v3 - s3',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x30, 0x83, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s0_1',
    label: 'v3 - s0-1',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x20, 0x80, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s1_1',
    label: 'v3 - s1-1',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x20, 0x81, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s2_1',
    label: 'v3 - s2-1',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x20, 0x82, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_v3/s3_1',
    label: 'v3 - s3-1',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x20, 0x83, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_vi3',
    label: 'vi3',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x21, 0x80, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_vi3/s1',
    label: 'vi3 - s1',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x21, 0x81, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_vi3/s2',
    label: 'vi3 - s2',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x21, 0x82, 0x00],
  }),
  new FanLampV2({
    id: 'lampsmart_pro_vi3/s3',
    label: 'vi3 - s3',
    app: LAMP_SMART,
    primary: false,
    deviceType: 0x0100,
    withSign: true,
    pfx: [0x21, 0x83, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_v3',
    label: 'v3',
    app: FAN_LAMP,
    primary: true,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x20, 0x80, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_v2',
    label: 'v2',
    app: FAN_LAMP,
    primary: true,
    deviceType: 0x0400,
    withSign: false,
    pfx: [0x10, 0x80, 0x00],
  }),
  new FanLampV1({
    id: 'fanlamp_pro_v1',
    label: 'v1',
    app: FAN_LAMP,
    primary: true,
    arg2: 0x83,
    arg2OnlyOnPair: false,
    header: [0x77, 0xf8],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_v3/se',
    label: 'v3 - Smart Elfin',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x10, 0x80, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_v3/s1',
    label: 'v3 - s1',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x20, 0x81, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_v3/s2',
    label: 'v3 - s2',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x20, 0x82, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_v3/s3',
    label: 'v3 - s3',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x20, 0x83, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_vi3',
    label: 'vi3',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x30, 0x80, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_vi3/s1',
    label: 'vi3 - s1',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x30, 0x81, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_vi3/s2',
    label: 'vi3 - s2',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x30, 0x82, 0x00],
  }),
  new FanLampV2({
    id: 'fanlamp_pro_vi3/s3',
    label: 'vi3 - s3',
    app: FAN_LAMP,
    primary: false,
    deviceType: 0x0400,
    withSign: true,
    pfx: [0x30, 0x83, 0x00],
  }),
];
/** All encodings belonging to one phone app. */
export function codecsForApp(app) {
  return CODECS.filter((c) => c.app === app);
}
/**
 * The encodings that app documents (v3/v2/v1). Short enough a list to sweep
 * inside the lamp's ~5 second pairing window.
 */
export function primaryCodecsForApp(app) {
  return CODECS.filter((c) => c.app === app && c.primary);
}
export function getCodec(id) {
  const c = CODECS.find((x) => x.id === id);
  if (!c) {
    throw new Error(`Unknown codec "${id}"`);
  }
  return c;
}
// ---------------------------------------------------------------------------
// Command vocabulary used by the UI
// ---------------------------------------------------------------------------
export const CMD = {
  pair: 0x28,
  unpair: 0x45,
  on: 0x10,
  off: 0x11,
  /** Dim warm glow. A real send path in the reference translator set. */
  nightMode: 0x23,
  /**
   * The next two are marked no_direct() in ha-ble-adv, meaning it only ever
   * DECODES them from a physical remote and never sends them. Sending them is
   * an extrapolation: the remote emits them, so the lamp should obey, but
   * unlike every other command here that is not verified against a reference
   * send path.
   */
  allOff: 0x6f,
  timer: 0x41,
};
/**
 * Sleep timer. `split_copy(ATTR_TIME, ['arg0','arg1'], 1/60, 256)` in the
 * reference means minutes split little-endian across two bytes.
 */
export function timerCmd(minutes) {
  const m = Math.max(0, Math.min(0xffff, Math.round(minutes)));
  return { cmd: CMD.timer, arg0: m & 0xff, arg1: (m >> 8) & 0xff };
}
/**
 * Split brightness + colour temperature into the two physical channels.
 *
 * `brightness` and `warmth` are both 0..1; warmth 0 is fully cold, 1 fully warm.
 * At warmth 0.5 both channels run flat out, which is how the official app
 * behaves -- it does not hold total output constant across the sweep.
 */
export function cwwChannels(brightness, warmth) {
  const br = Math.min(1, Math.max(0, brightness));
  const ct = Math.min(1, Math.max(0, warmth));
  return {
    cold: Math.round(br * Math.min(1, (1 - ct) * 2) * 255),
    warm: Math.round(br * Math.min(1, ct * 2) * 255),
  };
}
